import { Info as InfoIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface ThresholdPaceCalculatorProps {
  /**
   * Callback when user saves the calculated threshold pace
   * @param thresholdPaceSeconds - Calculated threshold pace in seconds per km
   */
  onSave: (thresholdPaceSeconds: number) => void;
}

/**
 * Threshold Pace Calculator Component
 *
 * Helps users calculate their threshold pace based on:
 * - T: All-out time (12min or 20min)
 * - D: Distance covered (km)
 * - P: Average pace (min/km)
 * - TP: Threshold pace (calculated)
 *
 * Formulas:
 * - P = T / D
 * - D = T / P
 * - TP = P × 0.95 (for 12min) or P × 0.98 (for 20min)
 * - P = TP / 0.95 (for 12min) or TP / 0.98 (for 20min)
 */
export const ThresholdPaceCalculator: React.FC<ThresholdPaceCalculatorProps> = ({ onSave }) => {
  const [open, setOpen] = useState(false);
  const [timeType, setTimeType] = useState<12 | 20>(12);
  const [distance, setDistance] = useState('');
  const [paceMinutes, setPaceMinutes] = useState('');
  const [paceSeconds, setPaceSeconds] = useState('');
  const [tpMinutes, setTpMinutes] = useState('');
  const [tpSeconds, setTpSeconds] = useState('');
  const [error, setError] = useState('');

  // Convert MM:SS to total seconds
  const paceToSeconds = (minutes: string, seconds: string): number | null => {
    const min = parseInt(minutes) || 0;
    const sec = parseInt(seconds) || 0;
    if (min === 0 && sec === 0) return null;
    return min * 60 + sec;
  };

  // Convert seconds to MM:SS
  const secondsToPace = (totalSeconds: number): { minutes: number; seconds: number } => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return { minutes, seconds };
  };

  // Auto-calculate fields when user fills 2 of 3
  useEffect(() => {
    const distanceVal = parseFloat(distance);
    const paceSec = paceToSeconds(paceMinutes, paceSeconds);
    const tpSec = paceToSeconds(tpMinutes, tpSeconds);

    // Count how many fields are filled
    const filledCount = [
      distanceVal > 0,
      paceSec !== null && paceSec > 0,
      tpSec !== null && tpSec > 0,
    ].filter(Boolean).length;

    // Only calculate if exactly 2 fields are filled
    if (filledCount !== 2) {
      setError(filledCount > 2 ? 'Please fill only 2 of the 3 fields (D, P, or TP)' : '');
      return;
    }

    setError('');

    // Case 1: D and P filled → Calculate TP
    if (distanceVal > 0 && paceSec !== null && paceSec > 0 && !tpSec) {
      const factor = timeType === 12 ? 0.95 : 0.98;
      const tpSeconds = paceSec * factor;
      const { minutes, seconds } = secondsToPace(tpSeconds);
      setTpMinutes(minutes.toString());
      setTpSeconds(seconds.toString());
      return;
    }

    // Case 2: D and TP filled → Calculate P
    if (distanceVal > 0 && tpSec !== null && tpSec > 0 && !paceSec) {
      const factor = timeType === 12 ? 0.95 : 0.98;
      const pSeconds = tpSec / factor;
      const { minutes, seconds } = secondsToPace(pSeconds);
      setPaceMinutes(minutes.toString());
      setPaceSeconds(seconds.toString());
      return;
    }

    // Case 3: P and TP filled → Calculate D
    if (paceSec !== null && paceSec > 0 && tpSec !== null && tpSec > 0 && !distanceVal) {
      // Calculate D from P: D = T / P
      const calculatedDistance = timeType / (paceSec / 60);
      setDistance(calculatedDistance.toFixed(2));
      return;
    }
  }, [distance, paceMinutes, paceSeconds, tpMinutes, tpSeconds, timeType]);

  const handleOpen = () => {
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    // Reset fields
    setTimeType(12);
    setDistance('');
    setPaceMinutes('');
    setPaceSeconds('');
    setTpMinutes('');
    setTpSeconds('');
    setError('');
  };

  const handleSave = () => {
    const tpSec = paceToSeconds(tpMinutes, tpSeconds);

    if (!tpSec || tpSec <= 0) {
      setError('Threshold pace must be calculated before saving');
      return;
    }

    onSave(tpSec);
    handleClose();
  };

  const handleTimeTypeChange = (value: 12 | 20) => {
    setTimeType(value);
    // Clear calculated values to force recalculation
    if (tpMinutes || tpSeconds) {
      setTpMinutes('');
      setTpSeconds('');
    }
  };

  return (
    <>
      <Tooltip title="Calculate threshold pace from race results" arrow>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Open threshold pace calculator"
          sx={{ ml: 0.5 }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <DialogTitle>Threshold Pace Calculator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fill in any <strong>2 of the 3 fields</strong> (Distance, Pace, or Threshold Pace) and
            the calculator will determine the rest. Based on an all-out effort over the selected
            time period.
          </Typography>

          {/* Time Type Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>All-Out Time</InputLabel>
            <Select
              value={timeType}
              onChange={e => handleTimeTypeChange(e.target.value as 12 | 20)}
              label="All-Out Time"
              aria-label="Select all-out time duration"
            >
              <MenuItem value={12}>12 minutes</MenuItem>
              <MenuItem value={20}>20 minutes</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              How long was your all-out effort?
            </Typography>
          </FormControl>

          {/* Distance Field */}
          <TextField
            fullWidth
            label="Distance (D)"
            type="number"
            value={distance}
            onChange={e => setDistance(e.target.value)}
            placeholder="e.g., 3.2"
            sx={{ mb: 2 }}
            aria-label="Distance covered in kilometers"
            InputProps={{
              endAdornment: (
                <Typography variant="caption" color="text.secondary">
                  km
                </Typography>
              ),
            }}
            inputProps={{ min: 0, step: 0.1 }}
            helperText="Distance covered during the all-out effort"
          />

          {/* Pace Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Average Pace (P)
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                label="Minutes"
                type="number"
                value={paceMinutes}
                onChange={e => setPaceMinutes(e.target.value)}
                inputProps={{ min: 0, max: 10 }}
                sx={{ width: 120 }}
                aria-label="Average pace minutes"
              />
              <Typography variant="h6">:</Typography>
              <TextField
                label="Seconds"
                type="number"
                value={paceSeconds}
                onChange={e => setPaceSeconds(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{ width: 120 }}
                aria-label="Average pace seconds"
              />
              <Typography variant="body2" color="text.secondary">
                / km
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Your average pace during the effort
            </Typography>
          </Box>

          {/* Threshold Pace Field */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Threshold Pace (TP)
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                label="Minutes"
                type="number"
                value={tpMinutes}
                onChange={e => setTpMinutes(e.target.value)}
                inputProps={{ min: 0, max: 10 }}
                sx={{ width: 120 }}
                aria-label="Threshold pace minutes"
              />
              <Typography variant="h6">:</Typography>
              <TextField
                label="Seconds"
                type="number"
                value={tpSeconds}
                onChange={e => setTpSeconds(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{ width: 120 }}
                aria-label="Threshold pace seconds"
              />
              <Typography variant="body2" color="text.secondary">
                / km
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Calculated threshold pace (×{timeType === 12 ? '0.95' : '0.98'})
            </Typography>
          </Box>

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} aria-label="Cancel threshold pace calculation">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!!error || !tpMinutes}
            aria-label="Save calculated threshold pace"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
